import {
  type BookSize,
  type DesignedCoverId,
} from '@mixtiles/web-backend-shared'
import { DEFAULT_PHOTO_RATIO } from 'services/PhotoBook/PhotoBook.consts'
import { type PhotoBookUpdater } from 'stores/photoBookDataStore/PhotoBookDataStore'
import { type Crop } from 'utils/cropUtils.types'
import {
  type AiGeneratedCover,
  type Caption,
  type Page,
  type PagePhoto,
  type PageSide,
} from './PhotoBook.types'
import {
  getBookCaption,
  isAiGeneratedCover,
  isPhotoCover,
} from './PhotoBook.utils'

/**
 * The domain edits of a photo book, as updaters for
 * `PhotoBookDataStore.updateBook`. Each one mutates the observable book in
 * place; deep observability makes every touched leaf reactive.
 */

// Swapping the cover's photo keeps the title the user gave the book, and turns
// a designed or generated cover back into a photo one.
export function setBookCoverPhoto(photo: PagePhoto): PhotoBookUpdater {
  return (book) => {
    book.cover = { type: 'photo', caption: getBookCaption(book.cover), photo }
  }
}

export function setBookDesignedCover(
  designedCoverId: DesignedCoverId
): PhotoBookUpdater {
  return (book) => {
    book.cover = {
      type: 'designed',
      designedCoverId,
      caption: getBookCaption(book.cover),
    }
  }
}

export function setBookAiGeneratedCover(
  aiCover: AiGeneratedCover
): PhotoBookUpdater {
  return (book) => {
    book.cover = { ...aiCover, bookCaption: getBookCaption(book.cover) }
  }
}

// An AI cover's caption is the headline printed on its spine, so title edits
// made while it's active go to the book's title kept beside it.
export function updateBookCoverCaption(
  caption: Partial<Caption>
): PhotoBookUpdater {
  return (book) => {
    const { cover } = book
    const currentCaption = getBookCaption(cover)
    const updatedCaption = {
      ...currentCaption,
      ...caption,
      fontHeight: caption.fontHeight ?? currentCaption.fontHeight ?? 0,
    }
    if (isAiGeneratedCover(cover)) {
      cover.bookCaption = updatedCaption
    } else {
      cover.caption = updatedCaption
    }
  }
}

export function updateBookCoverPhotoCrop(crop: Crop): PhotoBookUpdater {
  return (book) => {
    const { cover } = book
    if (!isPhotoCover(cover)) {
      throw new Error('Cannot crop a cover that has no photo')
    }

    cover.photo.cropParams[DEFAULT_PHOTO_RATIO] = crop
  }
}

// The editor's page transforms (paging, ordering, cropping, deletion) are pure
// and hand over the whole new sequence.
export function replaceBookPages(pages: Page[]): PhotoBookUpdater {
  return (book) => {
    book.pages = pages
  }
}

export function updateBookPagePhoto(
  pageIndex: number,
  pageSide: PageSide,
  photo: PagePhoto
): PhotoBookUpdater {
  return (book) => {
    const page = book.pages[pageIndex]
    if (!page) {
      throw new Error(`Photo book has no page at index ${pageIndex}`)
    }

    page[pageSide].photos = [photo]
  }
}

// Keeps the caption's font height, which the renderer measures rather than the
// caller choosing.
export function updateBookPagePhotoCaption(
  pageIndex: number,
  pageSide: PageSide,
  photoIndex: number,
  caption: Omit<Caption, 'fontHeight'>
): PhotoBookUpdater {
  return (book) => {
    const photo = book.pages[pageIndex]?.[pageSide].photos[photoIndex]
    if (!photo) {
      throw new Error(
        `Photo book has no photo at page ${pageIndex} ${pageSide} slot ${photoIndex}`
      )
    }

    photo.caption = { ...caption, fontHeight: photo.caption?.fontHeight || 0 }
  }
}

export function approveBookLowResPhotos(uids: string[]): PhotoBookUpdater {
  return (book) => {
    const { cover } = book
    if (isPhotoCover(cover)) {
      cover.photo.lowQualityApproved = true
    }

    book.pages.forEach((page) => {
      approvePageSidePhotos(page.front, uids)
      approvePageSidePhotos(page.back, uids)
    })
  }
}

export function addBookAiGeneratedCover(
  cover: AiGeneratedCover
): PhotoBookUpdater {
  return (book) => {
    book.aiGeneratedCovers.unshift(cover)
  }
}

export function setBookSize(size: BookSize): PhotoBookUpdater {
  return (book) => {
    book.size = size
  }
}

export function setBookQuantity(quantity: number): PhotoBookUpdater {
  return (book) => {
    book.quantity = quantity
  }
}

function approvePageSidePhotos(content: Page[PageSide], uids: string[]): void {
  if (!uids.includes(content.photos[0]?.uid)) {
    return
  }

  content.photos.forEach((photo) => {
    photo.lowQualityApproved = true
  })
}
